import { useEffect, useMemo, useState } from "react";

import { useProductsHook, useLanguageHook } from "../../../../Hooks/use-context-hooks";
import { filterComingSoonProducts } from "../../../../Utils/product-availability";
import { sortProductsByMsrpAndTitle } from "../../../../Utils/category-helper";

import ScrollableProductSections from "../../../../components/ScrollingComponent/ScrollContainer/ScrollableProductSections";

import styles from "./ProductsComingSoon.module.css";

const ALL_CATEGORIES = "ALL";

const CATEGORY_ORDER = [
  "Laundry",
  "Refrigeration",
  "Cooking",
  "Dishwasher",
  "LG Studio",
];

const CATEGORY_LABELS = {
  EN: {
    ALL: "All",
    Laundry: "Laundry",
    Refrigeration: "Refrigeration",
    Cooking: "Cooking",
    Dishwasher: "Dishwasher",
    "LG Studio": "LG Studio",
  },
  FR: {
    ALL: "Tous",
    Laundry: "Lessive",
    Refrigeration: "Réfrigérateur",
    Cooking: "Cuisson",
    Dishwasher: "Lave-vaisselle",
    "LG Studio": "LG Studio",
  },
};

const COMING_SOON_SECTION_TEXT = {
  EN: {
    title: "LG’s Latest Products",
    subtitle:
      "Get an early look at upcoming home appliances built to bring smart performance, thoughtful design, and everyday convenience into the home.",
  },
  FR: {
    title: "Les plus récentes nouveautés LG",
    subtitle:
      "Découvrez en avant-première les prochains électroménagers conçus pour offrir performance intelligente, design réfléchi et commodité au quotidien.",
  },
};

const ProductsComingSoon = ({
  products,
  title,
  subtitle,
  maxProducts,
  emptyState = null,
  showCount = true,
}) => {
  const { publicProducts } = useProductsHook();
  const { translatedText, language } = useLanguageHook();

  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);

  const sourceProducts = Array.isArray(products)
    ? products
    : publicProducts;

  const comingSoonProducts = useMemo(() => {
    const filteredProducts = filterComingSoonProducts(sourceProducts);

    const sortedMap = sortProductsByMsrpAndTitle({
      comingSoon: filteredProducts,
    });

    return sortedMap.comingSoon || [];
  }, [sourceProducts]);

  const availableCategories = useMemo(() => {
    const categorySet = new Set(
      comingSoonProducts
        .map(product => product.categoryEN || product.category)
        .filter(Boolean)
    );

    return CATEGORY_ORDER.filter(category => categorySet.has(category));
  }, [comingSoonProducts]);

  useEffect(() => {
    if (
      activeCategory !== ALL_CATEGORIES &&
      !availableCategories.includes(activeCategory)
    ) {
      setActiveCategory(ALL_CATEGORIES);
    }
  }, [activeCategory, availableCategories]);

  const visibleProducts = useMemo(() => {
    const filteredByCategory =
      activeCategory === ALL_CATEGORIES
        ? comingSoonProducts
        : comingSoonProducts.filter(product =>
            (product.categoryEN || product.category) === activeCategory
          );

    if (typeof maxProducts === "number") {
      return filteredByCategory.slice(0, maxProducts);
    }

    return filteredByCategory;
  }, [comingSoonProducts, activeCategory, maxProducts]);

  const sectionText =
    COMING_SOON_SECTION_TEXT[language] || COMING_SOON_SECTION_TEXT.EN;

  const labels = CATEGORY_LABELS[language] || CATEGORY_LABELS.EN;

  const categoryFilters = [
    ALL_CATEGORIES,
    ...availableCategories,
  ];

  const filterButtons = (
    <div className={styles.filterWrapper} aria-label="Product category filters">
      {categoryFilters.map(category => {
        const isActive = activeCategory === category;

        return (
          <button
            key={category}
            type="button"
            className={`${styles.filterButton} ${isActive ? styles.activeFilterButton : ""}`}
            onClick={() => setActiveCategory(category)}
            aria-pressed={isActive}
          >
            {labels[category] || category}
          </button>
        );
      })}
    </div>
  );

  return (
    <ScrollableProductSections
      sections={[
        {
          id: "coming-soon",
          title: title || sectionText.title,
          subtitle: subtitle || sectionText.subtitle,
          products: visibleProducts,
          headerElement: filterButtons,
        },
      ]}
      resultLabel={translatedText?.results || "results"}
      showCount={showCount}
      emptyState={emptyState}
    />
  );
};

export default ProductsComingSoon;